/**
 * 表示用の小さな Swift トークナイザ。クラス名は code.css（Chroma 互換）に合わせる。
 * HTML は作らない。行ごとのトークン列を返し、描画側が <span class> を組み立てる。
 * 厳密な構文解析はしない（読みやすさのための色付けだけ）。
 */

export interface Token {
  text: string;
  /** code.css のクラス。null は地の文字 */
  cls: string | null;
}

const KEYWORDS = new Set([
  'let',
  'var',
  'func',
  'return',
  'if',
  'else',
  'guard',
  'for',
  'in',
  'while',
  'repeat',
  'switch',
  'case',
  'default',
  'break',
  'continue',
  'struct',
  'class',
  'enum',
  'protocol',
  'extension',
  'import',
  'init',
  'self',
  'Self',
  'inout',
  'where',
  'throws',
  'throw',
  'try',
  'do',
  'catch',
  'defer',
  'mutating',
  'static',
  'private',
  'fileprivate',
  'public',
  'internal',
  'final',
  'as',
  'is',
  'some',
  'any',
  'async',
  'await',
  'typealias',
  'fallthrough',
  'lazy',
  'weak',
  'override',
]);
const CONSTANTS = new Set(['true', 'false', 'nil']);
const BUILTINS = new Set([
  'print',
  'readLine',
  'min',
  'max',
  'abs',
  'stride',
  'zip',
  'swap',
  'debugPrint',
  'fatalError',
  'precondition',
  'assert',
  'type',
]);

const RULES: [RegExp, string | null][] = [
  [/^\/\/.*/, 'c1'],
  [/^"(?:[^"\\]|\\.)*"?/, 's'],
  [/^[0-9][0-9_]*(?:\.[0-9_]+)?(?:e[+-]?[0-9]+)?/, 'mi'],
  [/^@[A-Za-z_][A-Za-z0-9_]*/, 'na'],
  [/^[A-Za-z_][A-Za-z0-9_]*/, 'ident'],
  [/^[+\-*/%=<>!&|^~?.:]+/, 'o'],
  [/^[()[\]{},;]/, 'p'],
  [/^\s+/, null],
];

function classifyIdent(word: string, rest: string): string {
  if (KEYWORDS.has(word)) return 'k';
  if (CONSTANTS.has(word)) return 'kc';
  if (BUILTINS.has(word) && rest.startsWith('(')) return 'nb';
  if (/^[A-Z]/.test(word)) return 'kt';
  if (rest.startsWith('(')) return 'nf';
  return 'nv';
}

export function highlightLine(line: string): Token[] {
  const out: Token[] = [];
  let rest = line;
  while (rest.length > 0) {
    let matched = false;
    for (const [re, cls] of RULES) {
      const m = re.exec(rest);
      if (!m) continue;
      const text = m[0];
      rest = rest.slice(text.length);
      const finalCls = cls === 'ident' ? classifyIdent(text, rest) : cls;
      const prev = out[out.length - 1];
      if (prev && prev.cls === finalCls) prev.text += text;
      else out.push({ text, cls: finalCls });
      matched = true;
      break;
    }
    if (!matched) {
      out.push({ text: rest.charAt(0), cls: null });
      rest = rest.slice(1);
    }
  }
  return out;
}

/** コード全体を行に分けて色付けする。末尾の改行 1 つは行として数えない */
export function highlightSwift(code: string): Token[][] {
  const lines = code.replace(/\r\n/g, '\n').replace(/\n$/, '').split('\n');
  return lines.map(highlightLine);
}
